import React from 'react';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import Dash from '@dashboard/components/Dash';
import { OrderTableCard, stripeIdRow } from '@dashboard/components/OrderPage';
import CouponCode from '@dashboard/components/DiscountsCoupons/CouponCode';
import { Badge } from '@dashboard/components/ui/badge';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatDateTime } from '@dashboard/lib/format-date';
import {
  COUPON_DURATION_LABEL,
  getCouponDiscountLabel,
  getCouponDurationOptionLabel,
} from '@dashboard/components/DiscountsCoupons/couponLabels';
import {
  CouponScopePage,
  SCOPE_LIST_HEADER,
} from '@dashboard/components/DiscountsCoupons/CouponScopeSummary';
import { EVERYWHERE_SENTENCE } from '@dashboard/components/DiscountsCoupons/couponRuleSentence';
import { scopePagesFromApi } from '@dashboard/components/DiscountsCoupons/scopePage';
import { DateTimeWithZone } from '@dashboard/components/TimeZoneLabel';
import CouponStats from './CouponStats';

const row = (label, value) => ({ label, value });

const legacyRows = coupon => {
  if (!coupon.type) return [];
  const pageType =
    coupon.type === 'charge' ? (
      'One-off payments'
    ) : coupon.type === 'subscription' ? (
      'Subscriptions'
    ) : (
      <Badge variant="info">Legacy</Badge>
    );
  return [
    row('Page type', pageType),
    {
      label: '',
      value:
        'This is a legacy coupon that only works on one-time payment links. To use this coupon for both one-time and subscription payments, please recreate it.',
      copy: null,
    },
  ];
};

const couponRows = coupon => [
  row('Label', coupon.label || <Dash />),
  {
    label: 'Code',
    value: coupon.code ? <CouponCode code={coupon.code} /> : <Dash />,
    copy: coupon.code || null,
  },
  ...legacyRows(coupon),
  row('Discount amount', getCouponDiscountLabel(coupon) || <Dash />),
  ...(coupon.amountOff ? [row('Currency', coupon.currency?.toUpperCase())] : []),
  row(COUPON_DURATION_LABEL, getCouponDurationOptionLabel(coupon) || <Dash />),
  row('Applies to setup fee', coupon.appliesToSetupFee ? 'Yes' : 'No'),
  row('Applies to booking fees', coupon.appliesToTicketFees ? 'Yes' : 'No'),
];

const scopeRows = coupon => {
  const pages = scopePagesFromApi(coupon);
  if (!pages.length) return [row('Checkouts and events', EVERYWHERE_SENTENCE)];
  const header = pages.length > 1 ? [{ key: 'header', label: SCOPE_LIST_HEADER, copy: null }] : [];
  return [
    ...header,
    ...pages.map(page => ({
      key: page._id,
      full: true,
      value: (
        <CouponScopePage
          page={page}
          appliesToSetupFee={coupon.appliesToSetupFee}
          appliesToTicketFees={coupon.appliesToTicketFees}
        />
      ),
    })),
  ];
};

const limitRows = coupon => [
  ...scopeRows(coupon),
  { label: 'Max uses', value: coupon.maxRedemptions || '∞', copy: coupon.maxRedemptions || null },
  row('Expires', coupon.redeemBy ? <DateTimeWithZone date={coupon.redeemBy} /> : "Doesn't expire"),
];

const DiscountsCouponsDetail = ({ coupon }) => {
  const { seller, isAdminLoggedInAsSeller } = useAuth();

  const detailRows = [
    row('Created on', coupon.createdAt ? formatDateTime(coupon.createdAt) : <Dash />),
    // Coupons carry no livemode, and the link has always opened for test and live alike.
    stripeIdRow({
      label: 'Stripe coupon ID',
      id: coupon.stripeId,
      path: `/coupons/${coupon.stripeId}`,
      livemode: true,
      seller,
      isAdminLoggedInAsSeller,
    }),
  ];

  return (
    <div className="w-full max-w-3xl">
      <Card className="mx-auto">
        <CardContent>
          <CouponStats coupon={coupon} />
        </CardContent>
      </Card>
      <OrderTableCard title="Coupon" rows={couponRows(coupon)} />
      <OrderTableCard title="Limit usage" rows={limitRows(coupon)} />
      <OrderTableCard title="Details" rows={detailRows} />
    </div>
  );
};

export default DiscountsCouponsDetail;
